import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Search, Share2, Monitor, ShoppingBag } from "lucide-react";
import EditorialSteps from "@/components/EditorialSteps";
import TechnologyStack from "@/components/TechnologyStack";
import { 
  heroVariant, 
  sectionVariant, 
  staggerContainer, 
  itemVariant,
  viewportOnce,
  viewportOnceNear
} from "@/lib/motion";

const platforms = [
  {
    category: "Search",
    items: ["Google Ads", "Microsoft Ads", "Apple Search Ads"],
  },
  {
    category: "Social",
    items: ["Meta (Facebook & Instagram)", "LinkedIn", "TikTok", "Pinterest", "X (Twitter)"],
  },
  {
    category: "Programmatic",
    items: ["Google DV360", "The Trade Desk", "Amazon DSP"],
  },
  {
    category: "Retail Media",
    items: ["Amazon Ads", "Walmart Connect", "Criteo"],
  },
];

const processSteps = [
  {
    number: "01",
    title: "Channel Assessment",
    description: "Evaluate which platforms and channels align with your audience and business objectives.",
  },
  {
    number: "02",
    title: "Unified Strategy",
    description: "Develop a cross-channel media strategy with coordinated messaging and budget allocation.",
  },
  {
    number: "03",
    title: "Campaign Activation",
    description: "Launch platform-specific campaigns with native creative and proper tracking.",
  },
  {
    number: "04",
    title: "Cross-Channel Optimization",
    description: "Analyze performance holistically and redistribute budget to highest-performing channels.",
  },
  {
    number: "05",
    title: "Learning & Expansion",
    description: "Apply learnings across channels and test new platforms as opportunities emerge.",
  },
];

const MediaActivations = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 md:pt-28 lg:pt-32 pb-16 md:pb-20 lg:pb-24">
        <div className="container-wide">
          {/* Header */}
          <motion.div 
            className="max-w-2xl mb-12 md:mb-16 lg:mb-20"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={heroVariant} className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-primary" />
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
                Media Activations
              </span>
            </motion.div>
            <motion.h1 variants={heroVariant} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-6">
              Platform expertise, channel agnostic.
            </motion.h1>
            <motion.p variants={heroVariant} className="text-lg text-muted-foreground leading-relaxed">
              We go where your audience is. Certified partnerships across 
              major platforms, unified by one strategy.
            </motion.p>
          </motion.div>

          {/* Platforms */}
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-12"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnceNear}
            variants={staggerContainer}
          >
            {platforms.map((platform) => (
              <motion.div key={platform.category} variants={itemVariant}>
                <h3 className="text-sm font-medium uppercase tracking-wide text-accent mb-4">
                  {platform.category}
                </h3>
                <ul className="space-y-3">
                  {platform.items.map((item) => (
                    <li key={item} className="text-muted-foreground text-sm">
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>

          {/* Approach */}
          <motion.div 
            className="mt-16 md:mt-20 lg:mt-24 pt-12 md:pt-14 lg:pt-16 border-t border-border"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            <div className="grid lg:grid-cols-2 gap-12">
              <motion.div variants={itemVariant}>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  Cross-channel, not siloed.
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We don't optimize channels in isolation. Every activation is part of 
                  a unified strategy, with shared learnings and coordinated budgets 
                  across platforms.
                </p>
              </motion.div>
              <motion.div variants={itemVariant}>
                <h2 className="text-2xl font-medium text-foreground mb-4">
                  Creative that performs.
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Platform-native creative built for performance. We test, iterate, 
                  and scale what works. Without sacrificing brand consistency.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* PROCESS - Editorial Steps */}
        <EditorialSteps 
          title="From strategy to activation."
          steps={processSteps}
        />

        {/* Technology Stack */}
        <TechnologyStack
          label="Platforms"
          title="Certified partnerships."
          description="We maintain direct partnerships with all major advertising platforms."
          categories={[
            {
              title: "Search",
              icon: Search,
              items: ["Google Ads", "Microsoft Ads", "Apple Search Ads", "Amazon Ads"]
            },
            {
              title: "Social",
              icon: Share2,
              items: ["Meta Ads", "TikTok Ads", "LinkedIn Ads", "Pinterest Ads"]
            },
            {
              title: "Programmatic",
              icon: Monitor,
              items: ["DV360", "The Trade Desk", "Amazon DSP", "Criteo"]
            },
            {
              title: "Retail Media",
              icon: ShoppingBag,
              items: ["Amazon Ads", "Walmart Connect", "Instacart Ads", "Target Roundel"]
            }
          ]}
        />

        <div className="container-wide">
          {/* CTA */}
          <motion.div 
            className="mt-12 md:mt-16 lg:mt-20 text-center"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={sectionVariant}
          >
            <Button size="lg" className="group" asChild>
              <Link to="/contact">
                Plan your media strategy
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MediaActivations;
